import { useAtom } from "jotai";
import { showsAtom, creedShowOfferAtom } from "../atoms/showAtoms";
import { Show, CreedShowOffer } from "@quantizr-engineering/common/types/show";
import apiClient from "./apiClient";
import { FIVE_MINUTES_MS } from "../utils/consts";
import { useEffect } from "react";
import { toast } from "sonner";

export const useShowService = () => {
  const [shows, setShows] = useAtom(showsAtom);

  useEffect(() => {
    cacheGetShows();
  }, []);

  const getShows = async () => {
    setShows((prev) => ({ ...prev, loading: true }));

    try {
      const response = await apiClient.get("/show", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setShows((prev) => ({
        loading: false,
        data: response.data,
        lastFetched: Date.now(),
      }));
      return response.data;
    } catch (error) {
      setShows((prev) => ({
        ...prev,
        loading: false,
        data: [],
        lastFetched: null,
      }));
      throw error;
    }
  };

  const cacheGetShows = async () => {
    if (!!shows.loading) {
      return;
    }
    if (
      !shows.lastFetched ||
      Date.now() - shows.lastFetched > FIVE_MINUTES_MS
    ) {
      await getShows();
    }
  };

  const postShow = async (show: Show) => {
    try {
      const response = await apiClient.post("/show", show, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      await getShows();
      toast.success("Show added");
      return response;
    } catch (error) {
      toast.error("Creating show failed!");
      throw error;
    }
  };

  const generateSettlementReport = async (show: Show, email: string) => {
    try {
      await apiClient.post(`/show/${show.id}/report/settlement`, {
        emailRecipient: email,
      });
      toast.success("Report Sent!");
    } catch (error) {
      toast.error("Failed to generate settlement report");
      throw error;
    }
  };

  const generateShowProposalReport = async (show: Show, email: string) => {
    try {
      await apiClient.post(`/show/${show.id}/report/proposal`, {
        emailRecipient: email,
      });
      toast.success("Report Sent!");
    } catch (error) {
      toast.error("Failed to generate show proposal report");
      throw error;
    }
  };

  const patchShow = async (show: Show) => {
    5;
    try {
      const response = await apiClient.patch(`/show/${show.id}`, show, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      await getShows();
      return response;
    } catch (error) {
      toast.error("Failed to update show");
      throw error;
    }
  };

  const deleteShow = async (show: Show) => {
    try {
      const response = await apiClient.delete(`/show/${show.id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      await getShows();
      return response;
    } catch (error) {
      toast.error("Failed to delete show");
      throw error;
    }
  };

  const uploadShowOfferStreamlined = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const res = await apiClient.post(`/show/upload/offer`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    setShows((prev) => ({
      loading: false,
      data: [...(prev.data ?? []), res.data],
      lastFetched: Date.now(),
    }));
    return res;
  };

  return {
    shows: shows.data,
    showsLoading: shows.loading,
    getShows,
    cacheGetShows,
    postShow,
    generateSettlementReport,
    generateShowProposalReport,
    uploadShowOfferStreamlined,
    patchShow,
    deleteShow,
  };
};

export const useShowOfferService = () => {
  const [creeds, setCreeds] = useAtom(creedShowOfferAtom);

  useEffect(() => {
    cacheGetCreedShowOffers();
  }, []);

  const getCreedShowOffers = async () => {
    setCreeds((prev) => ({ ...prev, loading: true }));

    try {
      const response = await apiClient.get("/show/creed-show-offer", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setCreeds((prev) => ({
        loading: false,
        data: response.data,
        lastFetched: Date.now(),
      }));
      return response.data;
    } catch (error) {
      setCreeds((prev) => ({
        ...prev,
        loading: false,
        data: [],
        lastFetched: null,
      }));
      throw error;
    }
  };

  const cacheGetCreedShowOffers = async () => {
    if (!!creeds.loading) {
      return;
    }
    if (
      !creeds.lastFetched ||
      Date.now() - creeds.lastFetched > FIVE_MINUTES_MS
    ) {
      await getCreedShowOffers();
    }
  };

  const patchCreedShowOffer = async (creedShowOffer: CreedShowOffer) => {
    try {
      const response = await apiClient.patch(
        `/show/creed-show-offer/${creedShowOffer.id}`,
        creedShowOffer,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      await getCreedShowOffers();
      toast.success(response.headers["qz-message"]);
      return response;
    } catch (error) {
      throw new Error("Unknown error.");
    }
  };

  return {
    creedShowOffers: creeds.data,
    creedShowOffersLoading: creeds.loading,
    getCreedShowOffers,
    cacheGetCreedShowOffers,
    patchCreedShowOffer,
  };
};
